import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import {
  UiMetaControllerService,
  UiOneTimeWebhookControllerService,
  UiAccountsControllerService,
} from 'src/app/cc-api/services';
import { FacetsPage } from 'src/app/facets/facets-page/facets-page.component';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';
import { Subscription, interval, switchMap, startWith } from 'rxjs';
import { NbPopoverDirective } from '@nebular/theme';
import { OnAPIErrorService } from '../../services/on-api-error.service';
import { whiteSpaceInputPattern } from '../../constants/inputPatterns';
import {
  alertStatusDescriptionMap,
  alertStatusTitleMapping,
  scriptLinkMap,
  cloudShellLinkMap,
} from './integration-constants';

@Component({
  selector: 'app-create-cloud-account-integration',
  templateUrl: './create-cloud-account-integration.component.html',
  styleUrls: ['./create-cloud-account-integration.component.scss'],
})
export class CreateCloudAccountIntegrationComponent extends FacetsPage {
  @Output() afterSaveAccount: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective; //copy popover
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() passedRef: any;
  @Input() cloudProviderType: any;
  accountName: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.pattern('^[a-z0-9-]+$'),
  ]);
  consoleCommand: string;
  private cpUrl: string = window.location.host;
  readonly ACCOUNTS_WRITE = 'ACCOUNTS_WRITE';
  generatingWebhookId: boolean = false;
  fetchingCpAccount: boolean = false;
  pollingWebhook: boolean = false;
  webhookSubscription: Subscription;
  pollingInterval: number = 5000;
  webhookId: string = '';
  alertTitle: string;
  alertType: string;
  alertDescription: string;
  enableStepTwo: boolean = false;
  webhookError: boolean = false;
  cloudShelllinkMap = cloudShellLinkMap;

  constructor(
    private activatedRoute: ActivatedRoute,
    private webhookIntegrationService: UiOneTimeWebhookControllerService,
    private accountsControllerService: UiAccountsControllerService,
    private customToast: CustomToastService,
    private uiMetaControllerService: UiMetaControllerService,
    private fb: UntypedFormBuilder,
  ) {
    super(activatedRoute);
  }

  OnPageLoad(p: Params): void {
    this.alertDescription = '';
    this.consoleCommand = '';
    this.webhookId = null;
    this.enableStepTwo = false;
    this.webhookError = false;
  }

  generateWebhook() {
    this.resetValues();
    this.generatingWebhookId = true;
    this.accountsControllerService
      .requestCloudAccountLinkingUsingPOST({
        accountName: this.accountName?.value,
        webhook: {
          type: this.cloudProviderType?.toUpperCase(),
        },
      })
      .subscribe(
        (data) => {
          this.webhookId = data?.id;
          if (this.cloudProviderType === 'AWS') {
            this.fetchCPAccount();
          } else {
            this.generateCommand();
          }
          this.generatingWebhookId = false;
          this.accountName?.disable();
          triggerAnalyticsEvent(
            `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_NEXT`,
          );
        },
        (error) => {
          this.generatingWebhookId = false;
          this.showErrorText(error);
        },
      );
  }

  fetchCPAccount() {
    this.fetchingCpAccount = true;
    this.uiMetaControllerService.getCPAccountUsingGET().subscribe(
      (cpAccount) => {
        this.fetchingCpAccount = false;
        this.generateCommand(cpAccount);
      },
      (error) => {
        this.fetchingCpAccount = false;
        this.showErrorText(error);
      },
    );
  }

  showErrorText(error) {
    this.webhookError = true;
    this.alertDescription =
      error?.error?.message ||
      'There was an issue proceeding to the next step. Please click "Next" once more to continue.';
    this.alertTitle = 'Failed to create account';
    this.alertType = 'danger';
    this.accountName.enable();
  }

  generateCommand(cpAccount?) {
    this.consoleCommand = `curl -sLO ${
      scriptLinkMap[this.cloudProviderType]?.scriptOne
    } && sh ${scriptLinkMap[this.cloudProviderType]?.scriptTwo} ${this.cpUrl} ${
      this.accountName?.value
    } ${this.webhookId} ${
      this.cloudProviderType === 'AWS' ? cpAccount?.accountId : ''
    }`;
    this.startPollingWebhook();
  }

  resetValues() {
    this.webhookError = false;
    this.alertDescription = '';
    this.alertTitle = '';
  }

  startPollingWebhook() {
    this.pollingWebhook = true;
    this.resetValues();
    this.enableStepTwo = true;
    this.webhookSubscription = interval(this.pollingInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.webhookIntegrationService.pollWebhookUsingGET(this.webhookId),
        ),
      )
      .subscribe(
        (response: any) => {
          if (response && response?.status !== 'WAITING') {
            this.pollingWebhook = false;
            this.webhookId = '';
            this.alertTitle = alertStatusTitleMapping[response?.status];
            if (response?.status === 'FAILED') {
              this.alertDescription = response?.errorMessage;
            } else {
              this.alertDescription =
                alertStatusDescriptionMap[response?.status];
            }
            this.alertType =
              response?.status === 'SUCCESS' ? 'success' : 'danger';
            this.afterSaveAccount.emit(this.accountName?.value);
            // If response is received, stop polling
            this.stopPolling();
            triggerAnalyticsEvent(
              `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_SUCCESSFUL`,
              {
                accountName: this.accountName?.value,
              },
            );
          }
        },
        (error) => {
          this.pollingWebhook = false;
          this.showErrorText(error);
          this.enableStepTwo = false;
          triggerAnalyticsEvent(
            `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_ERROR`,
          );
        },
      );
  }

  stopPolling() {
    if (this.webhookSubscription) {
      this.webhookSubscription.unsubscribe();
      this.pollingWebhook = false;
    }
  }

  resetInputs() {
    this.accountName.enable();
    this.enableStepTwo = false;
    this.consoleCommand = '';
    this.stopPolling();
    this.alertDescription = '';
    this.alertTitle = '';
    triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_BACK`);
  }

  closePopup() {
    triggerAnalyticsEvent(
      `ORG_SETTINGS_ACCOUNT_MANAGEMENT_${this.mode?.toUpperCase()}_CANCEL`,
    );
    this.stopPolling();
    this.passedRef.close();
  }
}
